/**
 * JourneyMap — current-state journey map with taxonomy, strengths, and pain points.
 * Shows competitor context (unique vs shared vs disadvantage) and customer segments.
 */

import { useState } from "react";
import type { JourneyStage, PainPoint, Strength, CompetitorContext } from "../types/diagnostic";
import {
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  MessageSquareQuote,
  Users,
  Target,
} from "lucide-react";

interface JourneyMapProps {
  stages: JourneyStage[];
  painPoints: PainPoint[];
  strengths?: Strength[];
  clientName: string;
}

/** Severity badge colors for pain points. */
const SEVERITY_STYLES: Record<string, string> = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-blue-100 text-blue-700",
};

/** Impact badge colors for strengths. */
const IMPACT_STYLES: Record<string, string> = {
  high: "bg-green-100 text-green-700",
  medium: "bg-emerald-100 text-emerald-700",
  low: "bg-teal-100 text-teal-700",
};

/** Competitor context badge styles and labels. */
const CONTEXT_CONFIG: Record<
  CompetitorContext,
  { label: string; style: string; icon: string }
> = {
  unique: {
    label: "Unique to client",
    style: "bg-purple-100 text-purple-700 border-purple-200",
    icon: "!",
  },
  shared: {
    label: "Industry-wide",
    style: "bg-slate-100 text-slate-600 border-slate-200",
    icon: "~",
  },
  disadvantage: {
    label: "Competitors do better",
    style: "bg-orange-100 text-orange-700 border-orange-200",
    icon: "v",
  },
};

export function JourneyMap({
  stages,
  painPoints,
  strengths = [],
  clientName,
}: JourneyMapProps) {
  const [expandedStage, setExpandedStage] = useState<string | null>(null);

  /** Get pain points for a given stage. */
  function getPainPointsForStage(stageId: string): PainPoint[] {
    return painPoints.filter((pp) => pp.stageId === stageId);
  }

  /** Get strengths for a given stage. */
  function getStrengthsForStage(stageId: string): Strength[] {
    return strengths.filter((s) => s.stageId === stageId);
  }

  /** Toggle a stage's expanded state. */
  function handleToggle(stageId: string) {
    setExpandedStage((prev) => (prev === stageId ? null : stageId));
  }

  return (
    <div className="mck-card p-6 space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">
          Current-State Customer Journey
        </h2>
        <p className="text-sm text-text-secondary mt-0.5">
          {clientName} — strengths and pain points across the B2B CX journey
        </p>
      </div>

      {/* Journey timeline */}
      <div className="space-y-0">
        {stages.map((stage, idx) => {
          const stagePainPoints = getPainPointsForStage(stage.id);
          const stageStrengths = getStrengthsForStage(stage.id);
          const hasItems =
            stagePainPoints.length > 0 || stageStrengths.length > 0;
          const isExpanded = expandedStage === stage.id;
          const hasHighSeverity = stagePainPoints.some(
            (pp) => pp.severity === "high"
          );
          const hasHighImpactStrength = stageStrengths.some(
            (s) => s.impact === "high"
          );

          const stageColor = hasHighSeverity
            ? "bg-red-100 text-red-700"
            : hasHighImpactStrength && stagePainPoints.length === 0
              ? "bg-green-100 text-green-700"
              : hasItems
                ? "bg-amber-100 text-amber-700"
                : "bg-mck-blue-100 text-mck-blue-700";

          return (
            <div key={stage.id} className="relative">
              {idx > 0 && (
                <div className="absolute left-5 -top-0 w-0.5 h-3 bg-surface-border" />
              )}

              <button
                onClick={() => handleToggle(stage.id)}
                className="w-full flex items-center gap-4 px-4 py-3 rounded-lg hover:bg-surface-secondary transition-colors text-left group"
              >
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${stageColor}`}
                >
                  {idx + 1}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-text-primary text-sm">
                      {stage.label}
                    </span>
                    {stageStrengths.length > 0 && (
                      <span className="text-xs text-green-700 bg-green-50 rounded-full px-2 py-0.5">
                        {stageStrengths.length} strength
                        {stageStrengths.length !== 1 ? "s" : ""}
                      </span>
                    )}
                    {stagePainPoints.length > 0 && (
                      <span className="text-xs text-text-muted bg-surface-tertiary rounded-full px-2 py-0.5">
                        {stagePainPoints.length} pain point
                        {stagePainPoints.length !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-tertiary mt-0.5 truncate">
                    {stage.description}
                  </p>
                </div>

                {hasItems && (
                  <span className="text-text-muted">
                    {isExpanded ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </span>
                )}
              </button>

              {/* Expanded items */}
              {isExpanded && hasItems && (
                <div className="ml-14 mr-4 mb-3 space-y-3 animate-fade-in">
                  {/* Strengths */}
                  {stageStrengths.map((s) => (
                    <div
                      key={s.id}
                      className="bg-green-50/50 rounded-lg p-4 border border-green-200/50"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <CheckCircle2
                          size={14}
                          className="text-green-600 mt-0.5"
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-text-primary">
                            {s.title}
                          </span>
                          <span
                            className={`ml-2 text-xs px-2 py-0.5 rounded-full ${IMPACT_STYLES[s.impact]}`}
                          >
                            {s.impact} impact
                          </span>
                        </div>
                      </div>
                      {/* Segments */}
                      {s.segments && s.segments.length > 0 && (
                        <SegmentTags segments={s.segments} />
                      )}
                      <QuoteList quotes={s.quotes} />
                    </div>
                  ))}

                  {/* Pain points */}
                  {stagePainPoints.map((pp) => (
                    <div
                      key={pp.id}
                      className="bg-surface-secondary rounded-lg p-4 border border-surface-borderLight"
                    >
                      {/* Title row */}
                      <div className="flex items-start gap-2 mb-2">
                        <AlertTriangle
                          size={14}
                          className={
                            pp.severity === "high"
                              ? "text-red-500 mt-0.5"
                              : pp.severity === "medium"
                                ? "text-amber-500 mt-0.5"
                                : "text-blue-500 mt-0.5"
                          }
                        />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-text-primary">
                            {pp.title}
                          </span>
                          <span
                            className={`ml-2 text-xs px-2 py-0.5 rounded-full ${SEVERITY_STYLES[pp.severity]}`}
                          >
                            {pp.severity}
                          </span>
                        </div>
                      </div>

                      {/* Competitor context + segments row */}
                      <div className="flex items-center gap-2 flex-wrap mb-3 ml-5">
                        {/* Competitor context badge */}
                        {pp.competitorContext && (
                          <CompetitorBadge
                            context={pp.competitorContext}
                            note={pp.competitorNote}
                          />
                        )}
                        {/* Segment tags */}
                        {pp.segments.length > 0 && (
                          <SegmentTags segments={pp.segments} />
                        )}
                      </div>

                      {/* Competitor note */}
                      {pp.competitorNote && (
                        <div className="flex items-start gap-1.5 ml-5 mb-3">
                          <Target
                            size={11}
                            className="text-text-muted mt-0.5 flex-shrink-0"
                          />
                          <p className="text-xs text-text-secondary leading-relaxed">
                            {pp.competitorNote}
                          </p>
                        </div>
                      )}

                      {/* Quotes */}
                      <QuoteList quotes={pp.quotes} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="space-y-2 pt-2 border-t border-surface-borderLight">
        <div className="flex items-center gap-4 text-xs text-text-tertiary flex-wrap">
          <span className="font-medium">Strengths:</span>
          {(["high", "medium", "low"] as const).map((imp) => (
            <span
              key={`s-${imp}`}
              className={`px-2 py-0.5 rounded-full ${IMPACT_STYLES[imp]}`}
            >
              {imp}
            </span>
          ))}
          <span className="mx-1 text-surface-border">|</span>
          <span className="font-medium">Pain points:</span>
          {(["high", "medium", "low"] as const).map((sev) => (
            <span
              key={`p-${sev}`}
              className={`px-2 py-0.5 rounded-full ${SEVERITY_STYLES[sev]}`}
            >
              {sev}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-text-tertiary flex-wrap">
          <span className="font-medium">Competitive context:</span>
          {(["unique", "disadvantage", "shared"] as const).map((ctx) => (
            <span
              key={ctx}
              className={`px-2 py-0.5 rounded-full border ${CONTEXT_CONFIG[ctx].style}`}
            >
              {CONTEXT_CONFIG[ctx].label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Competitor context badge. */
function CompetitorBadge({
  context,
  note,
}: {
  context: CompetitorContext;
  note?: string;
}) {
  const cfg = CONTEXT_CONFIG[context];
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${cfg.style}`}
      title={note}
    >
      {cfg.label}
    </span>
  );
}

/** Segment tags row. */
function SegmentTags({ segments }: { segments: string[] }) {
  return (
    <div className="inline-flex items-center gap-1.5 ml-0">
      <Users size={10} className="text-text-muted" />
      {segments.map((seg) => (
        <span
          key={seg}
          className="text-xs px-1.5 py-0.5 rounded bg-surface-tertiary text-text-tertiary"
        >
          {seg}
        </span>
      ))}
    </div>
  );
}

/** Reusable quote list for both strengths and pain points. */
function QuoteList({
  quotes,
}: {
  quotes: { source: string; snippet: string; date?: string; rating?: number }[];
}) {
  if (quotes.length === 0) return null;
  return (
    <div className="space-y-2 mt-3">
      {quotes.map((q, qi) => (
        <div key={qi} className="flex items-start gap-2 pl-1">
          <MessageSquareQuote
            size={12}
            className="text-text-muted mt-1 flex-shrink-0"
          />
          <div>
            <p className="text-xs text-text-secondary italic leading-relaxed">
              {q.snippet}
            </p>
            <p className="text-xs text-text-muted mt-0.5">
              — {q.source}
              {q.date ? ` · ${q.date}` : ""}
              {q.rating ? ` · ${q.rating}/5` : ""}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
